/**
 * useRoomCaptions — client-side hook powering real-time captions for premium
 * rooms. Two modes:
 *   • host mode  → records 8-second audio chunks + POSTs each to
 *                  `/api/rooms/{id}/caption` while `active` is true.
 *   • participant mode → polls `/api/rooms/{id}/captions?since=<ts>` every 2s
 *                        and appends new lines to the caption buffer.
 *
 * Returns { captions, lastText, start, stop, active } — `captions` is a
 * rolling ordered array of {id, text, speaker_display_name, created_at}.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "./api";

const CHUNK_MS = 8000; // 8-second chunks — good balance of latency vs Whisper cost
const POLL_MS = 2000;

export default function useRoomCaptions({ roomId, isHost, enabled, canCaption }) {
  const [captions, setCaptions] = useState([]);
  const [active, setActive] = useState(false);
  const captionsRef = useRef([]);
  const sinceRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  const appendCaption = useCallback((c) => {
    if (!c || !c.id) return;
    if (captionsRef.current.some((x) => x.id === c.id)) return;
    captionsRef.current = [...captionsRef.current, c].slice(-100);
    sinceRef.current = c.created_at;
    setCaptions([...captionsRef.current]);
  }, []);

  const pollOnce = useCallback(async () => {
    if (!enabled || !canCaption || !alive.current) return;
    try {
      const { data } = await api.get(`/rooms/${roomId}/captions`, {
        params: sinceRef.current ? { since: sinceRef.current, limit: 40 } : { limit: 20 },
      });
      (data || []).forEach(appendCaption);
    } catch (e) { /* silent — a paused/ended room is fine */ }
  }, [enabled, canCaption, roomId, appendCaption]);

  const uploadChunk = useCallback(async (blob) => {
    if (!blob || blob.size < 2000) return; // Skip tiny silences (< ~2 KB)
    const fd = new FormData();
    fd.append("audio", blob, "chunk.webm");
    try {
      const { data } = await api.post(`/rooms/${roomId}/caption`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.caption) appendCaption(data.caption);
    } catch (e) { /* silent */ }
  }, [roomId, appendCaption]);

  const start = useCallback(async () => {
    if (!isHost || !canCaption || !enabled) {
      setActive(false);
      return;
    }
    if (recorderRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = rec;
      // Restart the recorder every CHUNK_MS to force a discrete blob per chunk.
      rec.ondataavailable = async (e) => {
        if (e.data && e.data.size > 0) await uploadChunk(e.data);
      };
      rec.start(CHUNK_MS);
      chunkTimerRef.current = setInterval(() => {
        if (!recorderRef.current || recorderRef.current.state !== "recording") return;
        // Trigger a chunk boundary while still recording.
        recorderRef.current.requestData?.();
      }, CHUNK_MS);
      setActive(true);
    } catch (e) {
      setActive(false);
    }
  }, [isHost, canCaption, enabled, uploadChunk]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    recorderRef.current = null;
    setActive(false);
  }, []);

  // Poll every 2s while `enabled`. Even the host uses the poll loop so that
  // captions produced by their own uploads appear consistently.
  useEffect(() => {
    if (!enabled || !canCaption) return undefined;
    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, POLL_MS);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [enabled, canCaption, pollOnce]);

  // Auto-stop when the component consuming the hook toggles enabled off.
  useEffect(() => {
    if (!enabled) stop();
    return stop;
  }, [enabled, stop]);

  return { captions, active, start, stop };
}
